import React, { useRef } from 'react';
import { useRect } from '../../utils/useRect.util';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { MotifIconButton } from '../button/icon-button/icon-button';
import { ReactComponent as HardwareIcClose24px } from '../../assets/icons/navigation/ic_close_24px.svg';
import { ReactComponent as ActionIcCheckCircle24px } from '../../assets/icons/action/ic_check_circle_24px.svg';
import { ReactComponent as AlertIcError24px } from '../../assets/icons/alert/ic_error_24px.svg';
import { ReactComponent as AlertIcWarning24px } from '../../assets/icons/alert/ic_warning_24px.svg';
import { ReactComponent as ActionIcInfo24px } from '../../assets/icons/action/ic_info_24px.svg';

/**
 * @typedef Props
 * @prop {React.ReactNode} children
 * @prop {string} [className]
 * @prop {string} [actionName]
 * @prop {() => void} [actionOnClick]
 * @prop {'error' | 'success' | 'warning' | 'info'} [variant]
 * @prop {'top' | 'bottom'} [position]
 * @prop {() => void} [onClose]
 * @prop {{}} [closeButtonProps]
 * @prop {{}} [actionButtonProps]
 * @prop {{close: React.ReactNode, toast: React.ReactNode}} [icons]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifToast = (props) => {
  const {
    actionName = null,
    actionOnClick = null,
    children,
    className = '',
    variant = 'info',
    position = '',
    onClose = null,
    closeButtonProps = {},
    actionButtonProps = {},
    icons = {},
    ...other
  } = props;
  const actionRef = useRef(null);
  const rect = useRect(actionRef);
  const { width } = rect;

  const classes = classNames({
    'motif-toast': true,
    'motif-toast-success': variant === 'success',
    'motif-toast-error': variant === 'error',
    'motif-toast-warning': variant === 'warning',
    'motif-toast-info': variant === 'info',
    'motif-toast-fixed-top': position === 'top',
    'motif-toast-fixed-bottom': position === 'bottom',
    'motif-toast-has-action-button': actionName,
    [className]: true
  });

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      className={classes}
      {...other}
      data-motif-toast
    >
      <div className="motif-toast-content">
        <div className="motif-toast-icon-container">
          {icons.toast || (
            <React.Fragment>
              {variant === 'error' && <AlertIcError24px />}
              {variant === 'success' && <ActionIcCheckCircle24px />}
              {variant === 'warning' && <AlertIcWarning24px />}
              {variant === 'info' && <ActionIcInfo24px />}
            </React.Fragment>
          )}
        </div>
        <div
          className="motif-toast-text-container"
          style={{ width: `calc(100% - ${90 + width}px)` }}
        >
          {children}
        </div>
        {actionName && (
          <button
            className="motif-toast-action-button"
            onClick={actionOnClick}
            ref={actionRef}
            {...actionButtonProps}
          >
            <span className="motif-toast-action-name">{actionName}</span>
          </button>
        )}
        <MotifIconButton
          data-testid="toast-close"
          aria-label="Click here to remove toast"
          type="button"
          className="motif-toast-close-button"
          onClick={onClose}
          {...closeButtonProps}
        >
          {icons.close || <HardwareIcClose24px />}
        </MotifIconButton>
      </div>
    </div>
  );
};

MotifToast.propTypes = {
  actionName: PropTypes.string,
  actionOnClick: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string,
  onClose: PropTypes.func,
  position: PropTypes.oneOf(['', 'top', 'bottom']),
  closeButtonProps: PropTypes.instanceOf(Object),
  actionButtonProps: PropTypes.instanceOf(Object),
  variant: PropTypes.oneOf(['error', 'success', 'warning', 'info']),
  icons: PropTypes.shape({
    close: PropTypes.node,
    toast: PropTypes.node
  })
};

export { MotifToast };
